import { FC, useCallback, useState } from "react";
import { Form, FormikProvider } from "formik";
import { flatRatePlanNames } from "@common/flatRateBilling";
import { Drawer } from "@design-system/Drawer/Drawer";
import { IntegrationConnection } from "@common/types/integrationConnection";
import { AddClientMode } from "./AddClientSteps/lib";
import { AddClientDrawerStep } from "./AddClientDrawerSteps/lib";
import { AddClientDrawerStepsContainer } from "./AddClientDrawerStepsContainer";
import { useAddClientForm } from "./useAddClientForm";

interface Props {
    open: boolean;
    onClose: () => void;
    onAnimationEnd: (open: boolean) => void;
    initialMode?: AddClientMode;
    initialClientId?: string;
    isEntityLimitReached?: boolean;
}

export const AddClientDrawer: FC<Props> = ({
    open,
    onClose,
    onAnimationEnd,
    initialMode = "newClient",
    initialClientId,
    isEntityLimitReached = false,
}) => {
    const [currentStep, setCurrentStep] = useState<AddClientDrawerStep>(
        AddClientDrawerStep.SetupMethod,
    );
    const [mode, setMode] = useState<AddClientMode>(initialMode);
    const [clientId, setClientId] = useState<string | undefined>(
        initialClientId,
    );
    const [attempt, setAttempt] = useState(0);
    const [entityCount, setEntityCount] = useState(0);
    const [persistedClientName, setPersistedClientName] = useState("");
    const [persistedPlanLabel, setPersistedPlanLabel] = useState("");
    const [
        persistedBillingTransferEnabled,
        setPersistedBillingTransferEnabled,
    ] = useState(false);
    const [persistedInvitedCount, setPersistedInvitedCount] = useState(0);
    const [qboConnection, setQboConnection] =
        useState<IntegrationConnection | null>(null);

    const handleAddAnother = useCallback(() => {
        setMode("addEntity");
        setCurrentStep(AddClientDrawerStep.SetupMethod);
        setQboConnection(null);
        setAttempt((currentAttempt) => currentAttempt + 1);
    }, []);

    const handleAddAnotherClient = useCallback(() => {
        setMode("newClient");
        setClientId(undefined);
        setQboConnection(null);
        setEntityCount(0);
        setPersistedClientName("");
        setPersistedPlanLabel("");
        setPersistedBillingTransferEnabled(false);
        setPersistedInvitedCount(0);
        setCurrentStep(AddClientDrawerStep.SetupMethod);
        setAttempt((currentAttempt) => currentAttempt + 1);
    }, []);

    const { formik, loadingAction, submitWithoutInvite } = useAddClientForm({
        onClose,
        onSuccess: (createdClientId, values, invitedCount) => {
            setClientId(createdClientId);
            setEntityCount((count) => count + 1);
            if (mode === "newClient") {
                // Capture before formik.resetForm() runs in the multi-entity flow.
                setPersistedClientName(values.client.clientName);
                setPersistedPlanLabel(flatRatePlanNames[values.plan.plan]);
                setPersistedBillingTransferEnabled(
                    !values.plan.billingOwnership,
                );
                setPersistedInvitedCount(invitedCount);
            }
            if (mode === "newClient" && values.isMultiEntity === true) {
                handleAddAnother();
                return;
            }
            if (mode === "addEntity") {
                setCurrentStep(AddClientDrawerStep.AddAnotherEntity);
                return;
            }
            setCurrentStep(AddClientDrawerStep.Result);
        },
        multiEntityFlow: true,
        accountingConnection: qboConnection,
        mode,
        clientId,
        resetKey: attempt,
    });

    const clientName =
        persistedClientName ||
        formik.values.client.clientName ||
        formik.values.client.businessName;
    const planLabel =
        persistedPlanLabel || flatRatePlanNames[formik.values.plan.plan];

    return (
        <Drawer
            open={open}
            onClose={onClose}
            onAnimationEnd={onAnimationEnd}
            size="fullScreen"
            headerAddonLeft={mode === "addEntity" ? "Add entity" : "Add client"}
        >
            <FormikProvider key={`${mode}-${attempt}`} value={formik}>
                <Form>
                    <AddClientDrawerStepsContainer
                        currentStep={currentStep}
                        onStepChange={setCurrentStep}
                        onClose={onClose}
                        mode={mode}
                        initialMode={initialMode}
                        clientId={clientId}
                        isEntityLimitReached={isEntityLimitReached}
                        onAddAnotherEntity={handleAddAnother}
                        onAddAnotherClient={handleAddAnotherClient}
                        loadingAction={loadingAction}
                        qboConnection={qboConnection}
                        setQboConnection={setQboConnection}
                        entityCount={entityCount}
                        clientName={clientName}
                        planLabel={planLabel}
                        billingTransferEnabled={persistedBillingTransferEnabled}
                        invitedCount={persistedInvitedCount}
                        submitWithoutInvite={submitWithoutInvite}
                    />
                </Form>
            </FormikProvider>
        </Drawer>
    );
};
